/** Version of the game rules implemented by this package. Stored in every game record. */
export const RULES_VERSION = '0.1';

/**
 * Optional rules. Every default reproduces rules v0.1 exactly; the other values are
 * candidates for later versions and stay off unless a game explicitly turns them on.
 */
export interface RulesConfig {
  /**
   * What the Lookout does besides stepping: `pin` (rules v0.1) or `sight` (no special action;
   * under fog it sees farther and over terrain instead).
   */
  lookoutMode: 'pin' | 'sight';
  /** Fog of war (experimental). Turning it on switches the Lookout to `sight`. */
  fog: boolean;
  /** Draw when the same position occurs for the third time. */
  threefoldRepetition: boolean;
  /** Draw once this many full moves have been played (`null` means no limit). */
  moveLimit: number | null;
  /**
   * Compensation for moving second (experimental). `opening-build`: Basalt makes one free Mason
   * build before Ivory's first move.
   */
  secondPlayerCompensation: 'none' | 'opening-build';
  /**
   * How many of its own turns a Beacon must start on the summit to win (experimental; 1 in rules
   * v0.1).
   */
  summitHold: 1 | 2;
  /** A summit win does not count while an enemy piece stands next to the Beacon (experimental). */
  summitContested: boolean;
  /**
   * A harder summit (experimental): exactly one ramp pair leads up to it, and the other squares
   * around it are 3 levels below. Only affects generated mountains.
   */
  steepSummit: boolean;
  /**
   * Starting position (experimental). `corners`: each Beacon starts in a corner (Basalt a9, Ivory
   * i1) and Ivory's row is the point mirror of Basalt's, like the board.
   */
  startLayout: 'standard' | 'corners';
  /**
   * Summits (experimental). `twin`: no summit at e5; each camp has its own summit near a corner
   * (Basalt b8, Ivory h2) with relief in the centre, and a Beacon wins as soon as it reaches the
   * other camp's summit. `home`: each Beacon starts on its own peak (Basalt e9, Ivory e1), there is
   * no summit win, and a game is only won by capturing the enemy Beacon (or a stuck opponent).
   * `summitHold` and `summitContested` do not apply to either.
   */
  summitMode: 'center' | 'twin' | 'home';
  /** Whether each camp starts with a Lookout (experimental: `false` leaves d9 and d1 empty). */
  lookout: boolean;
  /**
   * The Beacon (experimental). `escorted`: it may only step while a friendly piece stands next to
   * it, and it can never be captured, so games are won at the summit (or by a stuck opponent).
   */
  beaconMode: 'free' | 'escorted';
  /** Whether the Mason may raise a non-summit square to the summit height. */
  masonMayBuildTo4: boolean;
  /**
   * Summit height, which is also the highest any square can be. 4 in rules v0.1; 6 is an
   * experimental "tall mountain" mode for playtesting.
   */
  maxHeight: 4 | 6;
}

export const DEFAULT_CONFIG: Readonly<RulesConfig> = Object.freeze({
  lookoutMode: 'pin',
  fog: false,
  threefoldRepetition: false,
  moveLimit: null,
  secondPlayerCompensation: 'none',
  summitHold: 1,
  summitContested: false,
  steepSummit: false,
  startLayout: 'standard',
  summitMode: 'center',
  lookout: true,
  beaconMode: 'free',
  masonMayBuildTo4: true,
  maxHeight: 4,
});

export function resolveConfig(overrides: Partial<RulesConfig> = {}): RulesConfig {
  const config: RulesConfig = { ...DEFAULT_CONFIG, ...overrides };
  if (config.fog && overrides.lookoutMode === undefined) config.lookoutMode = 'sight';
  if (config.fog && config.lookoutMode === 'pin') {
    throw new RangeError('Fog of war uses the Lookout in sight mode; the pin is not available');
  }
  if (config.moveLimit !== null && (!Number.isInteger(config.moveLimit) || config.moveLimit < 1)) {
    throw new RangeError(`moveLimit must be a positive integer or null, got ${config.moveLimit}`);
  }
  if (config.summitHold !== 1 && config.summitHold !== 2) {
    throw new RangeError(`summitHold must be 1 or 2, got ${String(config.summitHold)}`);
  }
  if (config.summitMode === 'home' && config.startLayout !== 'standard') {
    throw new RangeError('Home summits sit under the standard Beacon squares (e9 and e1)');
  }
  if (config.maxHeight !== 4 && config.maxHeight !== 6) {
    throw new RangeError(`maxHeight must be 4 or 6, got ${String(config.maxHeight)}`);
  }
  return config;
}
